import * as React from 'react';
import { StyleDTO } from 'core/api';
import { Colors } from 'core/ui';
import * as Images from 'pos/Images';

type Props<K extends { Style: StyleDTO }> = {
	// Introducir value y onClick(value: K) permite hacer funciones estables y evitar re-renders por cambios de props.
	// La alternativa es que desde fuera se haga un closure para tener el value asociado a cada botón, y eso fuerza
	// cambios de props continuamente porque la referencia al closure va cambiando
	value: K;
	onClick?: (value: K) => Promise<any>;
	// OJO: Si aparecen más props hay que pensar si hace falta tenerlas en cuenta en el React.memo o no.
	size: 'sm' | 'md' | 'lg' | 'phone-md' | 'phone-lg',
	disabled?: boolean;
	selected?: boolean;
	//Props para la edición de cantidades (AddinsStep)
	quantity?: number;
	editionMode?: boolean
	addProduct?: () => Promise<any>;
	removeProduct?: () => void;
};

// Meter receta para fijar tamaños o variantes si hace falta
export type ProductButtonProps<K extends { Style: StyleDTO }> = Props<K>; // & RecipeVariantProps<typeof slotRecipes.productButton>;

export const ProductButton = React.memo(function ProductButton<K extends { Style: StyleDTO }>({ onClick, disabled, size, selected, value, quantity, editionMode, addProduct, removeProduct }: ProductButtonProps<K>) {

	const style = value.Style;
	const backgroundColor = Colors.getBackgroundColorAsRgb(style.BackColor);
	const foregroundColor = backgroundColor ? Colors.getTextColor(backgroundColor) : '';

	const hasImage = !(style.ImageId === Images.NULL_IMAGE || style.ImageId === '');

	const handleClick = React.useCallback(async () => onClick?.(value), [onClick, value]);

	const className = [
		'btn-product',
		`btn-product-${size}`,
		selected ? 'btn-product-selected' : '',
		hasImage ? 'btn-product-has-image' : '',
		editionMode ? 'btn-product-edition' : '',
	].filter(Boolean).join(' ');

	return (
		<button
			type='button'
			disabled={disabled}
			className={className}
			style={{
				backgroundColor
			}}
			onClick={handleClick}
		>
			{quantity && quantity > 0 ?
				<span className='btn-product-quantity'>
					{quantity}
				</span>
				: null
			}
			{
				hasImage
					? <span className='btn-product-image-container'>
						<img
							src={Images.imgSrcFromId(style.ImageId)}
							className='btn-product-image'
							alt={style.Text}
						/>
					</span>
					: null
			}
			{style.Text ?
				<span
					className='btn-product-text'
					style={{
						color: foregroundColor
					}}
				>
					{style.Text}
				</span>
				: null
			}
			{editionMode ?
				<span className='btn-product-editor'>
					<span className='btn-product-editor-actions'>
						<span className='btn-product-editor-action' onClick={removeProduct}>
							<Images.Minus className='btn-product-editor-action-icon' />
						</span>

						<span className='btn-product-editor-separator' />

						<span className='btn-product-editor-action' onClick={addProduct}>
							<Images.Plus className='btn-product-editor-action-icon' />
						</span>
					</span>
				</span>
				: null
			}
		</button>
	);
}, (prev, next) =>
	// Memoizo en base a los valores estables que tengo: producto/familia, selección y disabled.
	// El resto de cosas (css, style, tamaño, ...) me dan igual porque deberían ser siempre iguales dado
	// un equipo y configuración.
	prev.value === next.value &&
	prev.selected === next.selected &&
	prev.disabled === next.disabled &&
	prev.quantity === next.quantity &&
	prev.editionMode === next.editionMode
) as <K extends { Style: StyleDTO }>(props: ProductButtonProps<K>) => React.JSX.Element;
